import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Search, ChevronRight, Wind, BookOpen, Heart, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const situations = [
  {
    id: 1,
    title: 'Mon ventre gargouille',
    keywords: ['ventre', 'gargouille', 'bruit', 'faim'],
    showBreathing: false,
    notMean: 'Mon ventre qui gargouille ne signifie pas automatiquement que j\'ai faim. Il peut simplement être en train de digérer, de faire circuler de l\'air, des liquides ou des aliments.',
    action: 'Buvez un verre d\'eau. Attendez 10 minutes. Puis demandez-vous si la faim est toujours là.',
    phrase: 'Mon ventre peut faire du bruit sans me demander à manger.'
  },
  {
    id: 2,
    title: 'J\'ai envie de sucre',
    keywords: ['sucre', 'envie', 'sucré', 'douceur'],
    showBreathing: false,
    notMean: 'Votre corps ne réclame pas forcément du sucre. Il peut réclamer une pause, du repos, du réconfort ou une décharge émotionnelle.',
    action: 'Faites une pause de 5 minutes. Changez d\'activité. Puis décidez ensuite, au lieu de répondre immédiatement à l\'envie.',
    phrase: 'Une envie de sucre n\'est pas toujours une faim. Parfois, c\'est une demande de pause déguisée.'
  },
  {
    id: 3,
    title: 'Je veux finir mon assiette alors que je n\'ai plus faim',
    keywords: ['finir', 'assiette', 'gâcher', 'plus faim'],
    showBreathing: false,
    notMean: 'Ce n\'est pas forcément un besoin du corps. C\'est peut-être un automatisme appris : finir, ne pas gâcher, faire plaisir, ne pas laisser.',
    action: 'Posez les couverts. Observez votre corps. Demandez-vous : "Est-ce que mon corps en veut encore, ou est-ce seulement mon habitude qui veut terminer ?"',
    phrase: 'Je peux m\'arrêter même s\'il reste quelque chose dans l\'assiette.'
  },
  {
    id: 4,
    title: 'J\'ai envie de grignoter devant la télé',
    keywords: ['grignoter', 'télé', 'écran', 'soir'],
    showBreathing: false,
    notMean: 'Ce n\'est pas forcément de la faim. C\'est peut-être l\'association automatique : écran = nourriture.',
    action: 'Gardez les mains occupées autrement : une tisane, une balle anti-stress, une couverture, une activité manuelle simple.',
    phrase: 'Ce n\'est pas la télé qui demande à manger. C\'est peut-être seulement une vieille association.'
  },
  {
    id: 5,
    title: 'J\'ai peur d\'avoir faim plus tard',
    keywords: ['peur', 'faim', 'plus tard', 'anticiper'],
    showBreathing: false,
    notMean: 'Ce n\'est pas forcément une faim présente. C\'est peut-être une anticipation, une peur du manque ou une tentative de sécurité.',
    action: 'Dites-vous : "Si j\'ai faim plus tard, je pourrai manger plus tard." Vous n\'êtes pas obligé(e) de manger maintenant pour résoudre une faim qui n\'existe pas encore.',
    phrase: 'Je n\'ai pas besoin de nourrir une faim future.'
  },
  {
    id: 6,
    title: 'Je mange parce que je suis triste',
    keywords: ['triste', 'tristesse', 'émotion', 'pleurer'],
    showBreathing: true,
    notMean: 'Ce n\'est pas une faim du ventre. C\'est peut-être une demande de consolation, de douceur, de présence ou de protection.',
    action: 'Identifiez l\'émotion : tristesse, fatigue, solitude, colère, déception, vide. Puis choisissez une réponse non alimentaire : vous poser, appeler quelqu\'un, écrire quelques lignes, sortir quelques minutes, écouter un audio de l\'application.',
    phrase: 'Ma tristesse a besoin d\'attention, pas forcément de nourriture.'
  },
  {
    id: 7,
    title: 'Je mange parce que je suis stressé(e)',
    keywords: ['stress', 'stressé', 'anxieux', 'tension'],
    showBreathing: true,
    notMean: 'Ce n\'est pas forcément un besoin alimentaire. C\'est peut-être une tentative de calmer le système nerveux rapidement.',
    action: 'Utilisez l\'outil de respiration pendant quelques minutes. Puis réévaluez l\'envie de manger.',
    phrase: 'Mon stress cherche une sortie. La nourriture n\'est pas la seule porte.'
  },
  {
    id: 8,
    title: 'Je me dis "foutu pour foutu"',
    keywords: ['foutu', 'abandon', 'craqué', 'lâcher'],
    showBreathing: false,
    notMean: 'Ce n\'est pas la vérité. C\'est une pensée-piège. Elle transforme un petit détour en abandon complet.',
    action: 'Dites-vous : "Un détour n\'annule pas le trajet." Puis reprenez normalement, sans punition, sans compensation excessive.',
    phrase: 'Ce n\'est pas parce qu\'une roue touche le bas-côté que je dois jeter la voiture dans le fossé.'
  },
  {
    id: 9,
    title: 'Je culpabilise après avoir mangé',
    keywords: ['culpabilité', 'culpabilise', 'honte', 'regrette'],
    showBreathing: false,
    notMean: 'La culpabilité ne répare rien. Elle augmente souvent la tension, la honte, et parfois l\'envie de recommencer.',
    action: 'Remplacez la culpabilité par une observation : "Qu\'est-ce qui s\'est passé juste avant ? Qu\'est-ce que je ressentais ? De quoi avais-je besoin ?"',
    phrase: 'Je n\'ai pas besoin de me punir. J\'ai besoin de comprendre le mécanisme.'
  },
  {
    id: 10,
    title: 'Je n\'arrive pas à savoir si j\'ai faim',
    keywords: ['savoir', 'faim', 'signal', 'confus'],
    showBreathing: false,
    notMean: 'Le signal peut être brouillé par l\'habitude, le stress, la fatigue, l\'envie ou l\'émotion.',
    action: 'Faites le test de l\'aliment bof : "Si je n\'avais qu\'un aliment peu attirant (des choux de Bruxelles), est-ce que j\'en mangerais ?" Si oui, c\'est probablement une vraie faim. Si non, c\'est peut-être une envie ou une émotion.',
    phrase: 'Si je ne mangerais que quelque chose de très attirant, ce n\'est peut-être pas de la faim.'
  },
  {
    id: 11,
    title: 'Je suis fatigué(e) et j\'ai envie de manger',
    keywords: ['fatigué', 'fatigue', 'épuisé', 'énergie'],
    showBreathing: true,
    notMean: 'Le corps réclame peut-être du repos, pas de la nourriture.',
    action: 'Faites une micro-pause. Buvez un verre d\'eau. Posez-vous quelques minutes. Puis réévaluez.',
    phrase: 'Quand je suis fatigué(e), manger n\'est pas toujours la meilleure recharge.'
  },
  {
    id: 12,
    title: 'Je suis invité(e) et j\'ai peur de trop manger',
    keywords: ['invité', 'repas', 'social', 'peur'],
    showBreathing: false,
    notMean: 'Ce n\'est pas une obligation de tout contrôler. Ce n\'est pas non plus une obligation de tout lâcher.',
    action: 'Choisissez ce qui vous fait vraiment plaisir. Mangez lentement. Laissez ce qui ne vaut pas vraiment le coup. L\'objectif n\'est pas d\'être parfait(e), mais de rester présent(e).',
    phrase: 'Je peux profiter sans me perdre.'
  },
  {
    id: 13,
    title: 'Je vais au restaurant',
    keywords: ['restaurant', 'sortie', 'menu', 'commander'],
    showBreathing: false,
    notMean: 'Ce n\'est pas une parenthèse perdue ni un danger. C\'est un moment de plaisir, sans basculer dans l\'excès automatique.',
    action: 'Commandez ce qui vous donne vraiment envie. Mangez lentement. Observez le moment où le plaisir commence à baisser. C\'est souvent un bon signal pour ralentir ou s\'arrêter.',
    phrase: 'Le plaisir compte, mais il n\'augmente pas forcément avec la quantité.'
  },
  {
    id: 14,
    title: 'J\'ai une pulsion alimentaire',
    keywords: ['pulsion', 'compulsion', 'irrésistible', 'urgence'],
    showBreathing: true,
    notMean: 'Une pulsion n\'est pas un ordre. C\'est une vague. Elle monte, elle atteint un sommet, puis elle redescend.',
    action: 'Attendez 10 minutes. Faites autre chose physiquement. Changez de pièce. Buvez un verre d\'eau. Puis réévaluez.',
    phrase: 'Une pulsion demande une réponse, pas forcément de la nourriture.'
  },
  {
    id: 15,
    title: 'Je mange en cachette',
    keywords: ['cachette', 'secret', 'caché', 'honte'],
    showBreathing: false,
    notMean: 'Ce n\'est pas seulement de la gourmandise. Il y a souvent de la honte, de l\'interdit, de la tension ou une émotion non dite.',
    action: 'Notez sans jugement : "Qu\'est-ce que je n\'ai pas voulu sentir ? Qu\'est-ce que je n\'ai pas osé dire ? Qu\'est-ce que je cherche à calmer ?" Ne pas culpabiliser. Observer le mécanisme.',
    phrase: 'Ce que je cache n\'a pas besoin d\'être puni. Ça a besoin d\'être compris.'
  },
  {
    id: 16,
    title: 'Je veux me peser plusieurs fois par jour',
    keywords: ['peser', 'balance', 'poids', 'obsession'],
    showBreathing: false,
    notMean: 'Ce n\'est pas du contrôle utile. C\'est souvent de l\'anxiété déguisée.',
    action: 'Reportez la pesée. Observez plutôt les comportements du jour : ai-je mangé plus lentement ? Ai-je écouté ma satiété ? Ai-je pris soin de moi ?',
    phrase: 'La balance mesure un poids. Elle ne mesure pas mes progrès invisibles.'
  },
  {
    id: 17,
    title: 'La balance monte après un repas salé ou copieux',
    keywords: ['balance', 'monte', 'salé', 'copieux', 'poids'],
    showBreathing: false,
    notMean: 'Ce n\'est pas forcément du gras. Le poids varie avec l\'eau, le sel, le transit, le cycle hormonal, le sommeil et le stress.',
    action: 'Attendez 48 heures avant d\'en tirer une conclusion. Reprenez simplement vos habitudes normales.',
    phrase: 'Une variation de poids n\'est pas un verdict.'
  },
  {
    id: 18,
    title: 'Je me sens gonflé(e)',
    keywords: ['gonflé', 'gonflée', 'ballonné', 'ventre'],
    showBreathing: false,
    notMean: 'Ce n\'est pas forcément une prise de poids. Cela peut être lié au transit, au sel, au cycle, au stress ou à la digestion.',
    action: 'Buvez un peu d\'eau. Marchez doucement. Évitez de paniquer. Ne prenez pas de décision radicale sous l\'effet de cette sensation.',
    phrase: 'Me sentir gonflé(e) ne signifie pas avoir grossi.'
  },
  {
    id: 19,
    title: 'Je n\'ai pas faim le matin',
    keywords: ['matin', 'petit-déjeuner', 'pas faim', 'forcer'],
    showBreathing: false,
    notMean: 'Ce n\'est pas forcément un problème. Tout le monde n\'a pas faim au même moment.',
    action: 'Ne vous forcez pas. Prévoyez simplement une option simple si la faim arrive plus tard.',
    phrase: 'Je peux écouter mon rythme sans transformer chaque différence en problème.'
  }
];

export const QueFaireSi = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [selectedSituation, setSelectedSituation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState([]);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/que-faire-si/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (e) {}
  };

  const getStat = (id) => stats.find(s => s.situation_id === id) || {};

  const interact = async (situationId, action) => {
    try {
      await axios.post(`${API_URL}/que-faire-si/interact`, {
        situation_id: situationId,
        action
      }, { headers: { Authorization: `Bearer ${token}` } });
      fetchStats();
    } catch (e) {}
  };

  const selectSituation = (situation) => {
    setSelectedSituation(situation);
    interact(situation.id, 'view');
  };

  // Sort: favorites first, then by view count, then by id
  const sortedSituations = [...situations].sort((a, b) => {
    const sa = getStat(a.id);
    const sb = getStat(b.id);
    if (sa.is_favorite && !sb.is_favorite) return -1;
    if (!sa.is_favorite && sb.is_favorite) return 1;
    return (sb.view_count || 0) - (sa.view_count || 0);
  });

  const filteredSituations = sortedSituations.filter(s =>
    s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.keywords.some(k => k.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const saveToJournal = async (situation) => {
    try {
      await axios.post(`${API_URL}/journal/entry`, {
        mood: 'pensif',
        note: `Que faire si... ${situation.title} — "${situation.phrase}"`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Enregistré dans votre carnet');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  // Écran de réponse
  if (selectedSituation) {
    return (
      <div className="min-h-screen bg-sand-50 pb-24" data-testid="que-faire-si-response">
        <div className="bg-gradient-to-b from-sage-100 to-sand-50 px-6 py-6">
          <button
            onClick={() => setSelectedSituation(null)}
            className="flex items-center gap-2 text-sage-600 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Retour aux situations</span>
          </button>
          <p className="text-xs text-sage-500 uppercase tracking-wide mb-1">Que faire si...</p>
          <h1 className="text-xl font-serif text-sage-700">
            {selectedSituation.title}
          </h1>
        </div>

        <div className="px-6 py-6 space-y-5">
          {/* Bloc 1 */}
          <div className="bg-white rounded-xl p-5 border border-sage-100">
            <p className="text-xs font-medium text-sage-500 uppercase tracking-wide mb-3">
              Ce que ça ne veut pas forcément dire
            </p>
            <p className="text-sage-700 text-sm leading-relaxed">
              {selectedSituation.notMean}
            </p>
          </div>

          {/* Bloc 2 */}
          <div className="bg-white rounded-xl p-5 border border-declic/20 border-l-4 border-l-declic">
            <p className="text-xs font-medium text-declic uppercase tracking-wide mb-3">
              Ce que vous pouvez faire maintenant
            </p>
            <p className="text-sage-700 text-sm leading-relaxed">
              {selectedSituation.action}
            </p>
          </div>

          {/* Bloc 3 */}
          <div className="bg-sage-600 rounded-xl p-5">
            <p className="text-xs font-medium text-white/60 uppercase tracking-wide mb-3">
              Phrase à retenir
            </p>
            <p className="text-white font-medium leading-relaxed">
              {selectedSituation.phrase}
            </p>
          </div>

          {/* Boutons d'action */}
          <div className="space-y-3 pt-4">
            {/* Favori + Résolu */}
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  const stat = getStat(selectedSituation.id);
                  interact(selectedSituation.id, stat.is_favorite ? 'unfavorite' : 'favorite');
                }}
                variant="outline"
                className={`flex-1 h-11 rounded-xl text-sm ${getStat(selectedSituation.id).is_favorite ? 'border-declic text-declic bg-declic/5' : 'border-sage-200 text-sage-600'}`}
              >
                <Heart className={`w-4 h-4 mr-2 ${getStat(selectedSituation.id).is_favorite ? 'fill-declic' : ''}`} />
                Favori
              </Button>
              <Button
                onClick={() => {
                  const stat = getStat(selectedSituation.id);
                  interact(selectedSituation.id, stat.is_resolved ? 'unresolve' : 'resolve');
                }}
                variant="outline"
                className={`flex-1 h-11 rounded-xl text-sm ${getStat(selectedSituation.id).is_resolved ? 'border-green-500 text-green-600 bg-green-50' : 'border-sage-200 text-sage-600'}`}
              >
                <CheckCircle2 className={`w-4 h-4 mr-2 ${getStat(selectedSituation.id).is_resolved ? 'text-green-500' : ''}`} />
                {getStat(selectedSituation.id).is_resolved ? 'Résolu' : 'Marquer résolu'}
              </Button>
            </div>

            {selectedSituation.showBreathing && (
              <Button
                onClick={() => navigate('/outils/coherence-cardiaque')}
                className="w-full bg-sage-100 hover:bg-sage-200 text-sage-700 h-11 rounded-xl text-sm"
              >
                <Wind className="w-4 h-4 mr-2" />
                Utiliser l'outil de respiration
              </Button>
            )}

            <Button
              onClick={() => saveToJournal(selectedSituation)}
              variant="outline"
              className="w-full border-sage-200 text-sage-600 h-11 rounded-xl text-sm"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Enregistrer dans mon carnet
            </Button>

            <Button
              onClick={() => setSelectedSituation(null)}
              variant="outline"
              className="w-full border-sage-200 text-sage-600 h-11 rounded-xl text-sm"
            >
              Choisir une autre situation
            </Button>

            <Button
              onClick={() => navigate('/outils')}
              variant="ghost"
              className="w-full text-sage-400 h-11 rounded-xl text-sm"
            >
              Quitter l'outil
            </Button>
          </div>
        </div>

        {/* Encadré de sécurité */}
        <div className="px-6 pb-8">
          <p className="text-xs text-sage-400 leading-relaxed">
            Cet outil ne remplace pas un avis médical. Si vous ressentez une douleur importante, des vomissements, un malaise, une déshydratation importante, des troubles alimentaires sévères ou des symptômes inhabituels, demandez un avis médical.
          </p>
        </div>
      </div>
    );
  }

  // Liste des situations
  return (
    <div className="min-h-screen bg-sand-50 pb-24" data-testid="que-faire-si-list">
      <div className="bg-gradient-to-b from-sage-100 to-sand-50 px-6 py-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/outils')}
          className="mb-4"
        >
          <ArrowLeft className="w-5 h-5 text-sage-700" />
        </Button>
        <h1 className="text-2xl font-serif font-light text-sage-700 mb-2">
          Que faire si...
        </h1>
        <p className="text-sage-500 text-sm mb-4">Choisissez la situation qui vous correspond maintenant.</p>

        {/* Barre de recherche */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sage-400" />
          <Input
            type="text"
            placeholder="Rechercher une situation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-white border-sage-200"
            data-testid="que-faire-si-search"
          />
        </div>
      </div>

      <div className="px-6 py-4 space-y-2">
        {filteredSituations.map((situation) => {
          const stat = getStat(situation.id);
          return (
            <button
              key={situation.id}
              onClick={() => selectSituation(situation)}
              className={`w-full flex items-center gap-3 p-4 bg-white rounded-xl border transition-colors text-left ${
                stat.is_resolved ? 'border-green-200 bg-green-50/30' : 'border-sage-100 hover:border-sage-300'
              }`}
              data-testid={`situation-${situation.id}`}
            >
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 ${
                stat.is_resolved ? 'bg-green-500 text-white' : stat.is_favorite ? 'bg-declic/10 text-declic' : 'bg-sage-100 text-sage-600'
              }`}>
                {stat.is_resolved ? <CheckCircle2 className="w-4 h-4" /> : stat.is_favorite ? <Heart className="w-3.5 h-3.5 fill-declic" /> : situation.id}
              </span>
              <span className="text-sm text-sage-700 flex-1">{situation.title}</span>
              <ChevronRight className="w-4 h-4 text-sage-400 flex-shrink-0" />
            </button>
          );
        })}

        {filteredSituations.length === 0 && (
          <p className="text-center text-sage-400 py-8 text-sm">Aucune situation trouvée</p>
        )}
      </div>

      {/* Encadré de sécurité */}
      <div className="px-6 py-4">
        <p className="text-xs text-sage-400 leading-relaxed">
          Cet outil ne remplace pas un avis médical. Si vous ressentez une douleur importante, des vomissements, un malaise, une déshydratation importante, des troubles alimentaires sévères ou des symptômes inhabituels, demandez un avis médical.
        </p>
      </div>
    </div>
  );
};

export default QueFaireSi;
